import { z } from 'zod';

import { AvailabilitySchema } from '@/shared/lib/availability';
import { MediaSchema } from '@/shared/lib/media';
import { MoneySchema } from '@/shared/lib/money';

// The browser sends only sku and quantity. Prices, discounts and totals are server-owned.

const SkuSchema = z.string().min(1).max(64);

export const AddCartLinesSchema = z
  .object({
    lines: z
      .array(
        z
          .object({
            sku: SkuSchema,
            quantity: z.number().int().min(1).max(99),
            /** Restores a removed line at its previous position ("Вернуть"). */
            position: z.number().int().min(0).max(99).nullable(),
          })
          .strict(),
      )
      .min(1)
      .max(10),
  })
  .strict();
export type AddCartLinesCommand = z.infer<typeof AddCartLinesSchema>;

export const UpdateCartLineSchema = z
  .object({
    quantity: z.number().int().min(1).max(99).nullable(),
    /** Replace the variant (colour/size) without removing the line. */
    sku: SkuSchema.nullable(),
  })
  .strict()
  .refine((value) => value.quantity !== null || value.sku !== null, {
    message: 'Нужно указать количество или вариант.',
  });
export type UpdateCartLineCommand = z.infer<typeof UpdateCartLineSchema>;

export const ApplyPromoSchema = z.object({ code: z.string().trim().min(1).max(32) }).strict();

export const CartLineSchema = z.object({
  lineId: z.string().min(1),
  sku: SkuSchema,
  productId: z.string(),
  productSlug: z.string(),
  productKind: z.enum(['item', 'bundle']),
  name: z.string(),
  media: MediaSchema.nullable(),
  tint: z.string().nullable(),
  colorId: z.string().nullable(),
  size: z.string().nullable(),
  variantLabel: z.string(),
  bundleParts: z.array(z.string()),
  quantity: z.number().int().min(1).max(99),
  unitPrice: MoneySchema,
  lineTotal: MoneySchema,
  availability: AvailabilitySchema,
  shipDate: z.string().nullable(),
  editable: z.boolean(),
});
export type CartLine = z.infer<typeof CartLineSchema>;

export const CartDiscountSchema = z.object({
  code: z.string(),
  label: z.string(),
  amount: MoneySchema,
});

export const CartSchema = z.object({
  id: z.string().nullable(),
  version: z.number().int().min(0),
  lines: z.array(CartLineSchema),
  itemsCount: z.number().int().min(0),
  subtotal: MoneySchema,
  discounts: z.array(CartDiscountSchema),
  discountTotal: MoneySchema,
  promo: z.object({ code: z.string(), amount: MoneySchema }).nullable(),
  total: MoneySchema,
  delivery: z.object({
    freeThreshold: MoneySchema,
    remaining: MoneySchema,
    isFree: z.boolean(),
    progress: z.number().min(0).max(100),
  }),
  bonusAccrual: z.number().int().min(0),
  installment: z.object({ count: z.number().int().min(2), amount: MoneySchema }).nullable(),
  hasPreorder: z.boolean(),
});
export type Cart = z.infer<typeof CartSchema>;

export const cartKeys = {
  all: () => ['cart'] as const,
  current: () => ['cart', 'current'] as const,
};
